import { Link } from "react-router-dom";
import { Clock, Utensils, Wallet, HandHeart, FileText, CalendarDays, ArrowRight, Zap, School, AlarmClock, HeartHandshake, Sparkles, Phone, Mail } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";
import PageHero from "@/components/PageHero";
import CTABand from "@/components/CTABand";
import { Reveal } from "@/components/Reveal";
import { FlowerDoodle, SproutDoodle, WavyUnderline, Bee } from "@/components/Doodles";
import { useLang } from "../lang/LanguageContext";

const SeedIcon = () => (
    <svg width="34" height="34" viewBox="0 0 34 34" aria-hidden="true">
        <ellipse cx="17" cy="18" rx="8" ry="10" fill="#A9805B" />
        <path d="M12 12c2-3 8-3 10 0" stroke="#3D3229" strokeWidth="2" strokeLinecap="round" fill="none" opacity="0.5" />
        <path d="M17 28v-8" stroke="#5E8355" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
);
const SproutIcon = () => <SproutDoodle size={36} />;
const BudIcon = () => (
    <svg width="34" height="34" viewBox="0 0 34 34" aria-hidden="true">
        <path d="M17 30V18" stroke="#5E8355" strokeWidth="2.6" strokeLinecap="round" />
        <ellipse cx="17" cy="12" rx="6.5" ry="8" fill="#F6C544" stroke="#3D3229" strokeWidth="1.8" />
        <path d="M17 6c-3 1.6-3 6.4 0 8M17 6c3 1.6 3 6.4 0 8" stroke="#D0684D" strokeWidth="1.6" fill="none" />
    </svg>
);
const FlowerIcon = () => <FlowerDoodle size={36} />;

const STEPS = [
    { icon: <SeedIcon />, title: "Schedule a tour", text: "Come by, meet Asia, and see the space. Kids welcome — they always end up in the sandbox." },
    { icon: <SproutIcon />, title: "Reserve your spot", text: "Save your spot with a security deposit equal to one week of tuition, plus the first week. No registration fee." },
    { icon: <BudIcon />, title: "Complete your packet", text: "NYS Blue Card, immunization record, medical statement, and our forms.", link: { to: "/forms", label: "See the enrollment forms" } },
    { icon: <FlowerIcon />, title: "First day!", text: "We'll help your child settle in — expect extra photos that day." },
];

const TUITION = [
    { id: "under2", name: "Under 2", ages: "6 weeks – 23 months", accent: "bg-skysoft border-sky", rates: ["3 days", "4 days", "5 days"] },
    { id: "ages2to5", name: "Ages 2–5", ages: "2–5 years", accent: "bg-sun/15 border-sun", rates: ["3 days", "4 days", "5 days"] },
];

const SCHOOL_AGE = [
    "Before School",
    "After School",
    "Before + After School",
    "School Closure / Break Full Day",
];

const GOOD_TO_KNOW = [
    { icon: Utensils, text: "Meals are included." },
    { icon: HandHeart, text: "Families with Nassau County DSS childcare assistance are welcome." },
    { icon: FileText, text: "Year-end tax statements with our EIN are provided each January." },
];

export default function Enroll() {
    usePageTitle("Enrollment Steps & Tuition | Daycare Massapequa Park NY");
    const { t } = useLang();

    return (
        <div>
            <PageHero
                eyebrow="Enrollment & tuition"
                title={<>Ready to Plant Roots With Us?</>}
                sub="Flexible hours for working families, clear rates, and a simple path from first hello to first day."
                doodle="sun"
            />

            {/* Openings banner */}
            <section className="pb-10">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <div
                            className="bg-sagedeep border-2 border-soil/20 rounded-[2rem] px-6 py-5 flex items-center gap-4 shadow-md"
                            data-testid="enroll-openings-banner"
                        >
                            <SproutDoodle size={40} />
                            <p className="font-display font-semibold text-lg md:text-xl text-white">
                                Now enrolling — call or text Asia to check current openings!
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* 4 steps */}
            <section className="pb-16 md:pb-20" aria-labelledby="steps-heading">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <h2 id="steps-heading" className="font-display font-semibold text-2xl md:text-3xl mb-10 md:mb-12">
                            Joining in 4 steps
                            <WavyUnderline className="block w-36 h-2.5 mt-2" color="#8FB38A" />
                        </h2>
                    </Reveal>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {STEPS.map((s, i) => (
                            <Reveal key={s.title} delay={i * 0.09} className="h-full">
                                <div className="hover-lift relative bg-white border-2 border-soil/10 rounded-3xl p-6 h-full" data-testid={`enroll-step-${i + 1}`}>
                                    <span className="absolute -top-4 right-5 font-display font-semibold text-5xl text-sage/25">{i + 1}</span>
                                    <span className="inline-flex p-2.5 rounded-2xl bg-creamsoft border-2 border-soil/10 mb-4">{s.icon}</span>
                                    <h3 className="font-display font-semibold text-xl mb-2">{s.title}</h3>
                                    <p className="text-soilmuted text-sm leading-relaxed">{s.text}</p>
                                    {s.link && (
                                        <Link
                                            to={s.link.to}
                                            data-testid="enroll-forms-link"
                                            className="mt-3 inline-flex items-center gap-1.5 text-sm font-extrabold text-sageink hover:gap-2.5 transition-all"
                                        >
                                            {s.link.label} <ArrowRight size={14} />
                                        </Link>
                                    )}
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* Hours & schedules */}
            <section className="pb-16 md:pb-20" aria-labelledby="hours-heading">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <h2 id="hours-heading" className="font-display font-semibold text-2xl md:text-3xl mb-8 md:mb-10">
                            Hours & Schedules
                            <WavyUnderline className="block w-40 h-2.5 mt-2" color="#F6C544" />
                        </h2>
                    </Reveal>
                    <div className="grid lg:grid-cols-2 gap-5 items-stretch">
                        <Reveal>
                            <div className="bg-creamsoft border-2 border-soil/10 rounded-3xl p-7 h-full" data-testid="enroll-hours-card">
                                <span className="inline-flex p-3 rounded-2xl bg-white border-2 border-soil/10 mb-4">
                                    <Clock size={24} className="text-sageink" />
                                </span>
                                <p className="text-lg font-bold text-soil">Monday–Friday: 6:00 AM – 8:00 PM</p>
                                <p className="text-soilmuted mt-1.5">Saturday: 9:00 AM – 6:00 PM</p>
                                <p className="text-sm text-soilmuted mt-4 leading-relaxed">
                                    We know working families need flexibility, so our hours are designed with you in mind. Each
                                    child has contracted drop-off and pick-up times set at enrollment. These help us plan staffing,
                                    keep the right caregiver-to-child ratios, and give your little one a consistent, comforting
                                    routine.
                                </p>
                                <div className="mt-6 flex gap-2" aria-hidden="true">
                                    <Bee size={38} />
                                    <span className="text-xs font-bold text-soil/40 self-end">Early birds welcome!</span>
                                </div>
                            </div>
                        </Reveal>
                        <Reveal delay={0.1}>
                            <div className="bg-sun border-2 border-soil/10 rounded-3xl p-7 h-full" data-testid="enroll-intro-pricing">
                                <span className="inline-flex p-3 rounded-2xl bg-white/70 mb-4">
                                    <Sparkles size={24} className="text-soil" />
                                </span>
                                <h3 className="font-display font-semibold text-2xl mb-3">Introductory pricing</h3>
                                <p className="text-soil leading-relaxed">
                                    For a limited time, tuition includes care anytime between 6:00 AM and 8:00 PM within your
                                    child's contracted schedule. There are no extra charges for early or late hours. Schedule
                                    changes just need to be approved in advance.
                                </p>
                                <p className="text-sm text-soil/70 mt-4">Rates and policies may change with at least two weeks' written notice.</p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Weekly tuition */}
            <section className="pb-16 md:pb-20" aria-labelledby="tuition-heading">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <h2 id="tuition-heading" className="font-display font-semibold text-2xl md:text-3xl mb-2">
                            Weekly tuition
                            <WavyUnderline className="block w-36 h-2.5 mt-2" color="#8FB38A" />
                        </h2>
                        <p className="text-soilmuted mb-8">
                            {t("Choose a 3–5 day schedule at enrollment (3-day minimum). Care anytime between 6:00 AM and 8:00 PM within your schedule is included.")}
                        </p>
                    </Reveal>
                    <div className="grid md:grid-cols-2 gap-5">
                        {TUITION.map((t, i) => (
                            <Reveal key={t.id} delay={i * 0.08} className="h-full">
                                <div className={`hover-lift border-2 rounded-[2rem] p-7 h-full ${t.accent}`} data-testid={`tuition-table-${t.id}`}>
                                    <h3 className="font-display font-semibold text-2xl">{t.name}</h3>
                                    <p className="text-sm font-extrabold uppercase tracking-wide text-soil/50 mb-4">{t.ages}</p>
                                    <ul className="space-y-2 text-[15px] font-bold text-soil">
                                        {t.rates.map((days) => (
                                            <li key={days} className="flex items-center gap-2.5">
                                                <span className="w-2 h-2 rounded-full bg-sageink/50" />
                                                {days}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal delay={0.12}>
                        <div className="mt-6 bg-sagedeep text-white rounded-[2rem] px-6 py-7 text-center" data-testid="enroll-rates-message">
                            <p className="font-display font-semibold text-xl md:text-2xl">{t("Let's talk tuition")}</p>
                            <p className="mt-2 text-white/85 leading-relaxed max-w-xl mx-auto">
                                {t("Rates are shared personally so we can find the right fit for your family. Please contact management to discuss current pricing for your child's age group and schedule.")}
                            </p>
                            <div className="mt-5 flex flex-col sm:flex-row justify-center gap-3">
                                <a
                                    href="tel:+15163243418"
                                    data-testid="rates-call-button"
                                    className="inline-flex items-center justify-center gap-2 bg-white text-sagedeep font-extrabold rounded-full px-6 py-3 hover:bg-cream transition-colors"
                                >
                                    <Phone size={18} /> {t("Call or text 516-324-3418")}
                                </a>
                                <a
                                    href="mailto:littleseedsflourish@gmail.com"
                                    data-testid="rates-email-button"
                                    className="inline-flex items-center justify-center gap-2 bg-white/15 border-2 border-white/40 text-white font-extrabold rounded-full px-6 py-3 hover:bg-white/25 transition-colors"
                                >
                                    <Mail size={18} /> {t("Email us")}
                                </a>
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.14}>
                        <p className="mt-6 text-soilmuted" data-testid="enroll-custom-schedules-note">
                            Need evening, partial-day, or another schedule not shown here? Custom schedules may be available.
                            Just ask and we'll find what works for your family.
                        </p>
                    </Reveal>
                    <Reveal delay={0.2}>
                        <div className="mt-6 bg-sun border-2 border-soil/10 rounded-[2rem] px-6 py-5 flex items-center gap-4" data-testid="enroll-sibling-discount">
                            <HeartHandshake size={26} className="text-soil shrink-0" />
                            <p className="font-bold text-soil">
                                Growing families get a little extra love! Families enrolling more than one child receive 10% off
                                the weekly tuition of the child with the lower tuition rate.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Drop-in, school-age, Saturday */}
            <section className="pb-16 md:pb-20" aria-labelledby="extras-heading">
                <div className="max-w-6xl mx-auto px-5 sm:px-8">
                    <Reveal>
                        <h2 id="extras-heading" className="font-display font-semibold text-2xl md:text-3xl mb-8 md:mb-10">
                            Extra flexibility, when you need it
                            <WavyUnderline className="block w-44 h-2.5 mt-2" color="#8FB38A" />
                        </h2>
                    </Reveal>
                    <div className="grid md:grid-cols-3 gap-5">
                        <Reveal className="h-full">
                            <div className="bg-creamsoft border-2 border-soil/10 rounded-3xl p-7 h-full" data-testid="enroll-extra-days-card">
                                <span className="inline-flex p-2.5 rounded-xl bg-white border-2 border-soil/10 mb-4">
                                    <Zap size={22} className="text-sageink" />
                                </span>
                                <h3 className="font-display font-semibold text-xl mb-3">Extra days</h3>
                                <p className="text-sm text-soilmuted leading-relaxed">
                                    For enrolled families only. Extra days must be arranged in advance and are subject to
                                    availability. We do not offer drop-in care for children who are not enrolled.
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.08} className="h-full">
                            <div className="bg-creamsoft border-2 border-soil/10 rounded-3xl p-7 h-full" data-testid="enroll-schoolage-card">
                                <span className="inline-flex p-2.5 rounded-xl bg-white border-2 border-soil/10 mb-4">
                                    <School size={22} className="text-sageink" />
                                </span>
                                <h3 className="font-display font-semibold text-xl mb-1">School-age care</h3>
                                <p className="text-xs font-extrabold uppercase tracking-wide text-soil/50 mb-3">Kindergarten–12 years</p>
                                <ul className="space-y-2 text-[15px] font-bold text-soil">
                                    {SCHOOL_AGE.map((label) => (
                                        <li key={label} className="flex items-center gap-2.5">
                                            <span className="w-2 h-2 rounded-full bg-sageink/50" />
                                            {label}
                                        </li>
                                    ))}
                                </ul>
                                <p className="text-sm text-soilmuted mt-4 leading-relaxed">
                                    Please note that we do not provide transportation to or from school.
                                </p>
                                <p className="text-sm text-soilmuted mt-3 leading-relaxed">
                                    Weekly packages cover the same set days each week, paid weekly.
                                </p>
                            </div>
                        </Reveal>
                        <Reveal delay={0.12} className="h-full">
                            <div className="bg-creamsoft border-2 border-soil/10 rounded-3xl p-7 h-full" data-testid="enroll-saturday-card">
                                <span className="inline-flex p-2.5 rounded-xl bg-white border-2 border-soil/10 mb-4">
                                    <CalendarDays size={22} className="text-sageink" />
                                </span>
                                <h3 className="font-display font-semibold text-xl mb-3">Saturday care</h3>
                                <p className="text-sm text-soilmuted leading-relaxed">
                                    Saturday care is available to enrolled families by request only and must be arranged and
                                    approved in advance. Up to 9 hours of care. Subject to availability and not guaranteed.
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Registration, payment, late pick-up */}
            <section className="pb-16 md:pb-20" aria-labelledby="registration-heading">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-3 gap-5 items-start">
                    <Reveal>
                        <div className="bg-sage/10 border-2 border-sage/25 rounded-3xl p-7 h-full" data-testid="enroll-registration-card">
                            <span className="inline-flex p-2.5 rounded-xl bg-white border-2 border-soil/10 mb-4">
                                <FileText size={22} className="text-sageink" />
                            </span>
                            <h2 id="registration-heading" className="font-display font-semibold text-2xl mb-4">Registration & enrollment</h2>
                            <ul className="space-y-3 text-[15px] text-soilmuted leading-relaxed">
                                <li><b className="text-soil">Registration fee:</b> None.</li>
                                <li><b className="text-soil">Security deposit:</b> Equal to one week of your child's contracted weekly tuition. If paying the full deposit upfront is difficult, an installment arrangement may be available.</li>
                                <li><b className="text-soil">First week's tuition:</b> Due before your child's first day.</li>
                            </ul>
                            <p className="text-sm text-soilmuted mt-4 leading-relaxed">
                                Your security deposit is applied toward your child's final week of tuition when two weeks'
                                written withdrawal notice is given and your account is in good standing.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <div className="bg-white border-2 border-soil/10 rounded-3xl p-7 h-full" data-testid="enroll-payment-card">
                            <span className="inline-flex p-2.5 rounded-xl bg-sun/25 mb-4">
                                <Wallet size={22} className="text-soil" />
                            </span>
                            <h2 className="font-display font-semibold text-2xl mb-4">Payment policy</h2>
                            <p className="text-[15px] text-soilmuted leading-relaxed">
                                Weekly tuition is due Sunday for the upcoming week of care. Payments received after Monday at
                                9:00 AM are considered late, and a $5 per day late fee applies until paid. Payments may be
                                made by cash, Zelle, Venmo, or check.
                            </p>
                            <p className="text-[15px] text-soilmuted leading-relaxed mt-3">
                                Tuition reserves your child's space and is due regardless of absences, illness, or family
                                vacations, as outlined in our Parent Handbook.
                            </p>
                        </div>
                    </Reveal>
                    <Reveal delay={0.12}>
                        <div className="bg-white border-2 border-soil/10 rounded-3xl p-7 h-full" data-testid="enroll-latepickup-card">
                            <span className="inline-flex p-2.5 rounded-xl bg-sun/25 mb-4">
                                <AlarmClock size={22} className="text-soil" />
                            </span>
                            <h2 className="font-display font-semibold text-2xl mb-4">Late pick-up</h2>
                            <p className="text-[15px] text-soilmuted leading-relaxed">
                                Children should be picked up by their contracted pick-up time. A late fee of $15 for each 15
                                minutes or portion thereof applies when a child stays past their contracted time without an
                                approved schedule change. All children must be picked up by our 8:00 PM closing time.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* Good to know + closures */}
            <section className="pb-16 md:pb-24">
                <div className="max-w-6xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 gap-6">
                    <Reveal>
                        <div className="bg-sage/10 border-2 border-sage/25 rounded-3xl p-7 h-full" data-testid="enroll-good-to-know-card">
                            <h2 className="font-display font-semibold text-2xl mb-5">Good to know</h2>
                            <ul className="space-y-4">
                                {GOOD_TO_KNOW.map((g) => (
                                    <li key={g.text} className="flex items-center gap-3.5">
                                        <span className="inline-flex p-2.5 rounded-xl bg-white border-2 border-soil/10 shrink-0">
                                            <g.icon size={19} className="text-sageink" />
                                        </span>
                                        <span className="font-bold text-soil">{g.text}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                    <Reveal delay={0.1}>
                        <div className="bg-creamsoft border-2 border-soil/10 rounded-3xl p-7 h-full" data-testid="enroll-closures-card">
                            <span className="inline-flex p-3 rounded-2xl bg-white border-2 border-soil/10 mb-4">
                                <CalendarDays size={24} className="text-coraldeep" />
                            </span>
                            <h2 className="font-display font-semibold text-2xl mb-4">We're closed</h2>
                            <ul className="space-y-2.5 text-soilmuted font-semibold">
                                <li className="flex gap-2.5"><FlowerDoodle size={18} petals="#E07A5F" className="mt-1 shrink-0" /> New Year's Day</li>
                                <li className="flex gap-2.5"><FlowerDoodle size={18} petals="#E07A5F" className="mt-1 shrink-0" /> Thanksgiving & the day after</li>
                                <li className="flex gap-2.5"><FlowerDoodle size={18} petals="#E07A5F" className="mt-1 shrink-0" /> Christmas Eve & Christmas Day</li>
                                <li className="flex gap-2.5"><FlowerDoodle size={18} petals="#E07A5F" className="mt-1 shrink-0" /> 10 vacation days a year (calendar shared each January)</li>
                            </ul>
                        </div>
                    </Reveal>
                </div>
            </section>

            <CTABand title="Let's plan your first visit." />
        </div>
    );
}
